import { proveNever } from "@glideapps/ts-necessities";
import type { AppFacilities } from "common/components/types";
import type { Database } from "common/Database";
import type { EminenceFlags } from "common/Database/eminence";
import type {
    AllQuotaValuesWithLimitsForKinds,
    QuotaValues,
    QuotaValuesForKinds,
    QuotaValuesWithLimit,
} from "common/Database/quotas";
import {
    makeQuotaDocumentID,
    QuotaContext,
    quotaInfos,
    QuotaKind,
    quotasCollectionName,
    quotasFromDocument,
} from "common/Database/quotas";
import { logError } from "common/debug-print";
import { getPersistentDeviceID } from "common/persistent-device-id";
import { defined, isOlderThan } from "common/Support";
import { getIsOnline } from "hooks/use-network-status";
import entries from "lodash/entries";
import fromPairs from "lodash/fromPairs";
import { standalone } from "webapp/lib/device";
import { getQuotasKey, localStorageGetItem, localStorageSetItem } from "webapp/lib/local-storage";
import * as routes from "../routes";
import { isDocumentHidden, onVisibilityChange, removeOnVisibilityChangeHandler } from "./browser-hacks";

const offlineHeartbeatPeriod = 60 * 1000;
const onlineHeartbeatPeriod = 15 * 60 * 1000;
const heartbeatCheckPeriod = 10 * 1000;

const getHeartbeatPeriod = () => (getIsOnline() ? onlineHeartbeatPeriod : offlineHeartbeatPeriod);

export function loadQuotasFromLocalStorage(appID: string): QuotaValuesForKinds | undefined {
    const fromStorage = localStorageGetItem(getQuotasKey(appID));
    if (fromStorage === undefined) return undefined;

    return JSON.parse(fromStorage) as QuotaValuesForKinds;
}

function saveQuotasToLocalStorage(appID: string, quotas: QuotaValuesForKinds): void {
    localStorageSetItem(getQuotasKey(appID), JSON.stringify(quotas));
}

class HeartbeatManager {
    private intervalHandle: unknown = undefined;
    private lastTime = new Date(Date.now() - getHeartbeatPeriod());
    private refcount = 0;
    private currentlyForEditor: boolean = false;
    private sendingForEditor: boolean = false;

    constructor(private readonly appID: string, private readonly appFacilities: AppFacilities) {}

    public enter(): void {
        if (this.refcount === 0) {
            onVisibilityChange(this.handleVisibilityChange);
        }
        this.refcount++;
        if (this.intervalHandle === undefined) {
            this.resetInterval();
        }
    }

    public exit(): void {
        if (this.refcount > 0) {
            this.refcount--;
        }
        if (this.refcount === 0) {
            removeOnVisibilityChangeHandler(this.handleVisibilityChange);
        }
    }

    private resetInterval(): void {
        this.lastTime = new Date(Date.now() - getHeartbeatPeriod());
        if (this.intervalHandle !== undefined) {
            clearTimeout(this.intervalHandle as any);
        }
        this.intervalHandle = setInterval(() => this.handleHeartbeat(), heartbeatCheckPeriod);
        // We want to fire the heartbeat immediately if we've requested a reset.
        this.handleHeartbeat();
    }

    private shouldHandleHeartbeat(): boolean {
        return !isDocumentHidden() && isOlderThan(this.lastTime, getHeartbeatPeriod());
    }

    private handleHeartbeat(): void {
        if (!this.shouldHandleHeartbeat()) {
            return;
        }
        if (this.refcount < 1) {
            clearTimeout(this.intervalHandle as any);
            this.intervalHandle = undefined;
        }
        this.lastTime = new Date();
        // The significance of the `editor` boolean is that only tables used in
        // the app will be reloaded on automatic refreshes if this is not set to `true`.
        // So, unless you open the Data Editor, periodic refreshes and drive notifications
        // will only cause updates to tables actually used in the app.
        //
        // This does have the side effect of not showing new tables until either
        // 1. The Data Editor has been open at least once
        // 2. The "reload" button has been pressed
        //
        // We'd do this with beaconCloudFunctionWeb, except between 2017 and 2020
        // Chrome wouldn't sendBeacon with JSON data to work around its own internal
        // security SNAFU. ensureDataLiveliness expects actual JSON, and will continue
        // to do so, so no beaconing here.
        this.appFacilities
            .callAuthIfAvailableCloudFunction(
                "ensureDataLiveliness",
                {
                    appID: this.appID,
                    editor: this.sendingForEditor,
                    deviceID: getPersistentDeviceID(this.appID),
                    standalone,
                    builder: !routes.isPlayer(),
                },
                {}
            )
            .catch(e => logError(`Could not ensureDataLiveliness: ${e}`));
        if (!this.currentlyForEditor) {
            this.sendingForEditor = false;
        }
    }

    private handleVisibilityChange = () => {
        if (this.shouldHandleHeartbeat()) {
            this.resetInterval();
        }
    };

    public enterEditor(): void {
        if (!this.sendingForEditor) {
            this.resetInterval();
        }
        this.sendingForEditor = true;
        this.currentlyForEditor = true;
        this.enter();
    }

    public exitEditor(): void {
        this.currentlyForEditor = false;
        this.exit();
    }
}

export abstract class HeartbeatAndQuotaManager {
    private _heartbeatManager: HeartbeatManager;
    private _mountCount: number = 0;
    private _quotaUnlisten: (() => void) | undefined;
    private _quotas: QuotaValuesForKinds = {};
    private _quotasFromLocalStorage: QuotaValuesForKinds | undefined;

    constructor(private readonly appID: string, appFacilities: AppFacilities, private readonly database: Database) {
        this._heartbeatManager = new HeartbeatManager(appID, appFacilities);
    }

    protected abstract getEminenceFlags(): EminenceFlags;
    protected abstract getRowQuotaOverride?(): number | undefined;
    protected abstract quotasDidUpdate?(
        newQuotas: AllQuotaValuesWithLimitsForKinds,
        oldQuotas: AllQuotaValuesWithLimitsForKinds
    ): void;
    protected abstract updateQuotaReached?(kind: QuotaKind, reached: boolean): void;

    private loadStoredQuotas(): QuotaValuesForKinds | undefined {
        if (this._quotasFromLocalStorage === undefined) {
            this._quotasFromLocalStorage = loadQuotasFromLocalStorage(this.appID);
        }
        return this._quotasFromLocalStorage ?? (window as any).glideQuotaState;
    }

    public getQuotaValues(kind: QuotaKind): QuotaValuesWithLimit | undefined {
        const eminenceFlags = this.getEminenceFlags();
        let defaultLimit: number;
        switch (kind) {
            case QuotaKind.RowsUsed:
                defaultLimit = this.getRowQuotaOverride?.() ?? eminenceFlags.rowQuota;
                break;
            case QuotaKind.ZapsAndWebhook:
                defaultLimit = eminenceFlags.zapQuota;
                break;
            case QuotaKind.Reloads:
                defaultLimit = eminenceFlags.reloadQuota;
                break;
            case QuotaKind.Signatures:
                defaultLimit = eminenceFlags.signatureQuota;
                break;
            case QuotaKind.DeliverEmail:
                defaultLimit = eminenceFlags.deliverEmailQuota;
                break;
            case QuotaKind.FileBytesUsed:
                defaultLimit = eminenceFlags.fileStorageQuota;
                break;
            case QuotaKind.BarcodesScanned:
                defaultLimit = eminenceFlags.barcodeScanQuota;
                break;
            default:
                return proveNever(kind, "Invalid quota kind", undefined);
        }

        let values = this._quotas[kind];
        if (values === undefined) {
            const storedQuotas = this.loadStoredQuotas();
            values = storedQuotas?.[kind];
        }

        return {
            current: values?.current ?? 0,
            limit: values?.limit ?? defaultLimit,
        };
    }

    private get allQuotas(): AllQuotaValuesWithLimitsForKinds {
        const pairs = quotaInfos.map(
            qi => [qi.kind, defined(this.getQuotaValues(qi.kind))] as [QuotaKind, QuotaValues]
        );
        return fromPairs(pairs) as AllQuotaValuesWithLimitsForKinds;
    }

    private updateQuotas(quotas: QuotaValuesForKinds): void {
        const prior = this.allQuotas;
        for (const [k, values] of entries(quotas)) {
            if (values === undefined) continue;

            const kind = k as QuotaKind;

            const old = this._quotas[kind];
            if (old === undefined) {
                this._quotas = { ...this._quotas, [kind]: values };
            } else {
                const updates: QuotaValues = { current: values.current, limit: values.limit ?? old.limit };
                this._quotas = { ...this._quotas, [kind]: updates };
            }

            const newValues = this.getQuotaValues(kind);
            if (newValues === undefined) continue;
            const reached = newValues.current >= newValues.limit;
            this.updateQuotaReached?.(kind, reached);
        }

        saveQuotasToLocalStorage(this.appID, this.allQuotas);

        this.quotasDidUpdate?.(this.allQuotas, prior);
    }

    private updateQuotaListening(): void {
        if (this._mountCount > 0 && this._quotaUnlisten === undefined) {
            this._quotaUnlisten = this.database.listenToDocument(
                quotasCollectionName,
                makeQuotaDocumentID(QuotaContext.App, this.appID),
                data => this.updateQuotas(quotasFromDocument(this.database, data).quotas)
            );
        } else if (this._mountCount <= 0 && this._quotaUnlisten !== undefined) {
            this._quotaUnlisten();
            this._quotaUnlisten = undefined;
        }
    }

    public isMounted(inEditor: boolean): void {
        if (inEditor) {
            this._heartbeatManager.enterEditor();
        } else {
            this._heartbeatManager.enter();
        }

        this._mountCount += 1;
        this.updateQuotaListening();
    }

    public isUnmounted(inEditor: boolean): void {
        if (inEditor) {
            this._heartbeatManager.exitEditor();
        } else {
            this._heartbeatManager.exit();
        }

        this._mountCount -= 1;
        this.updateQuotaListening();
    }
}

export class SimpleHeartbeatAndQuotaManager extends HeartbeatAndQuotaManager {
    constructor(
        appID: string,
        appFacilities: AppFacilities,
        database: Database,
        private readonly eminenceFlags: EminenceFlags
    ) {
        super(appID, appFacilities, database);
    }

    protected getEminenceFlags(): EminenceFlags {
        return this.eminenceFlags;
    }

    protected getRowQuotaOverride?(): number | undefined;
    protected quotasDidUpdate?(
        newQuotas: AllQuotaValuesWithLimitsForKinds,
        oldQuotas: AllQuotaValuesWithLimitsForKinds
    ): void;
    protected updateQuotaReached?(kind: QuotaKind, reached: boolean): void;
}
